import { useState, useEffect, useRef, useCallback } from 'react';
import Layout from '../components/Layout';
import { clipsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Play, Eye, Volume2, VolumeX } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import ClipCreator from '../components/ClipCreator';
import usePageTitle from '../hooks/usePageTitle';
import { toast } from 'sonner';

const Clips = () => {
  usePageTitle('Clips');
  const { user } = useAuth();
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [muted, setMuted] = useState(true);
  const containerRef = useRef(null);
  const videoRefs = useRef({});

  const fetchClips = useCallback(async () => {
    try {
      const res = await clipsAPI.feed({ limit: 50 });
      setClips(res.data);
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchClips(); }, [fetchClips]);

  // Intersection observer for auto-play
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const idx = parseInt(entry.target.dataset.index);
          const video = videoRefs.current[idx];
          if (entry.isIntersecting) {
            setActiveIndex(idx);
            if (video) { video.currentTime = 0; video.play().catch(() => {}); }
            // Mark as viewed
            if (clips[idx]) clipsAPI.view(clips[idx].clip_id).catch(() => {});
          } else {
            if (video) video.pause();
          }
        });
      },
      { threshold: 0.7 }
    );

    const container = containerRef.current;
    if (container) {
      container.querySelectorAll('[data-clip-card]').forEach(el => observer.observe(el));
    }
    return () => observer.disconnect();
  }, [clips]);

  const timeAgo = (date) => {
    const mins = Math.floor((Date.now() - new Date(date).getTime()) / 60000);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-dark-slate"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 sticky top-0 z-20 bg-frost-white/80 dark:bg-dark-slate/80 backdrop-blur-md">
          <h1 className="font-heading font-bold text-xl text-dark-slate dark:text-frost-white">Clips</h1>
          <Button
            size="sm"
            onClick={() => setShowCreate(true)}
            className="rounded-full bg-mint-fresh text-white hover:bg-mint-fresh/90"
          >
            <Plus className="w-4 h-4 mr-1" /> Create
          </Button>
        </div>

        {clips.length === 0 ? (
          <div className="text-center py-20 px-4">
            <div className="w-16 h-16 rounded-full bg-mint-fresh/10 flex items-center justify-center mx-auto mb-4">
              <Play className="w-8 h-8 text-mint-fresh" />
            </div>
            <h2 className="font-heading font-bold text-lg text-dark-slate dark:text-frost-white mb-2">No Clips Yet</h2>
            <p className="text-slate-gray text-sm mb-4">Be the first to share a moment with the GradLink community!</p>
            <Button
              onClick={() => setShowCreate(true)}
              className="rounded-full bg-mint-fresh text-white hover:bg-mint-fresh/90"
            >
              <Plus className="w-4 h-4 mr-1" /> Create Your First Clip
            </Button>
          </div>
        ) : (
          <div ref={containerRef} className="snap-y snap-mandatory overflow-y-auto" style={{ height: 'calc(100vh - 120px)' }}>
            {clips.map((clip, i) => (
              <div
                key={clip.clip_id}
                data-clip-card
                data-index={i}
                className="snap-start relative bg-black flex items-center justify-center"
                style={{ height: 'calc(100vh - 120px)' }}
              >
                {clip.media_type === 'video' ? (
                  <video
                    ref={el => { videoRefs.current[i] = el; }}
                    src={clip.media_url}
                    loop
                    muted={muted}
                    playsInline
                    className="max-h-full max-w-full object-contain"
                    onClick={() => {
                      const v = videoRefs.current[i];
                      if (v) v.paused ? v.play() : v.pause();
                    }}
                  />
                ) : (
                  <img src={clip.media_url} alt="" className="max-h-full max-w-full object-contain" />
                )}

                {/* Overlay info */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 pb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Avatar className="w-8 h-8 border-2 border-white">
                      <AvatarImage src={clip.user_photo} />
                      <AvatarFallback className="text-xs">{clip.user_name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <span className="text-white font-semibold text-sm">{clip.user_name}</span>
                    <span className="text-white/50 text-xs">{timeAgo(clip.created_at)}</span>
                  </div>
                  {clip.caption && <p className="text-white text-sm">{clip.caption}</p>}
                </div>

                {/* Side actions */}
                <div className="absolute right-3 bottom-24 flex flex-col gap-4">
                  <div className="text-center">
                    <Eye className="w-5 h-5 text-white mx-auto" />
                    <span className="text-white text-xs">{clip.views || 0}</span>
                  </div>
                  {clip.media_type === 'video' && (
                    <button onClick={() => setMuted(!muted)} className="text-white">
                      {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ClipCreator open={showCreate} onOpenChange={setShowCreate} onClipCreated={fetchClips} />
    </Layout>
  );
};

export default Clips;
